import type { UIMatch } from 'react-router';
import type { RouteHandle } from '@/routes/Router';

import { useMatches } from 'react-router';
import { Box, useMediaQuery, useTheme, AppBar } from '@mui/material';

import { routes } from '@/constants/Constants';
import { useUrlChecker } from '@/hooks/useUrlChecker';
import { useMenu, SIDEBAR } from '@/appWrapper/SideMenu';
import useProfileDrawerStore from '@/stores/useProfileDrawerStore';
import { useAnalyticsContext } from '@/utils/analytics/AnalyticsContext';
import { useSmartBack } from '@/shared/hooks';

import { MobileMenuButton } from './MobileMenuButton';
import { BackButton } from './BackButton';
import { ProfileButton } from './ProfileButton';
import { mergeSx } from '@/shared/utils/merge-sx.util';
import { SignUpLink } from './SignUpLink';

export const SiteHeader = () => {
  const theme = useTheme();
  const styles = getStyles();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { isSigningUp, isPublic } = useUrlChecker();
  const preRegister = isPublic || window.location.pathname === routes.unitsOwnedEnroll;
  const { openMenu, menuOpen } = useMenu();
  const { showProfileDrawer } = useProfileDrawerStore();
  const { track } = useAnalyticsContext();
  const matches = useMatches() as UIMatch<unknown, RouteHandle>[];
  const currentRoute = matches[matches.length - 1];
  const hasBreadcrumbs = currentRoute?.handle?.hasBreadcrumbs === true;

  const handleBack = useSmartBack({ fallbackPath: '/', minHistoryEntries: 1 });

  const onProfile = () => {
    track('Profile Button is clicked', {
      title: 'Profile',
      referrer: window.location.pathname,
    });
    showProfileDrawer();
  };

  const sideMenuWidth = isMobile ? 0 : menuOpen ? SIDEBAR.width.expanded : SIDEBAR.width.collapsed;
  const sidebarTransition = `${SIDEBAR.transition.durationMs} ${SIDEBAR.transition.easing}`;

  const showMobileMenu = isMobile && !preRegister;
  const showDesktopBack = !preRegister && hasBreadcrumbs && !isMobile;
  const showSigningUpBack = isSigningUp;
  const showProfile = !isPublic && !isSigningUp;

  return (
    <AppBar
      sx={mergeSx(styles.appBar, {
        left: `${sideMenuWidth}px !important`,
        maxWidth: `calc(100vw - ${sideMenuWidth}px)`,
        transition: `left ${sidebarTransition}, max-width ${sidebarTransition}`,
        padding: isMobile ? '8px 16px' : '10px 12px',
      })}
    >
      <Box sx={styles.itemsGroup}>
        {showMobileMenu && <MobileMenuButton onClick={openMenu} />}
        {showDesktopBack && <BackButton onClick={handleBack} variant="full" />}
        {showSigningUpBack && <BackButton onClick={handleBack} variant="compact" />}
      </Box>
      <Box sx={styles.itemsGroup}>
        {isSigningUp && <SignUpLink />}
        {showProfile && <ProfileButton onClick={onProfile} />}
      </Box>
    </AppBar>
  );
};

const getStyles = () => ({
  appBar: {
    position: 'sticky',
    zIndex: 600,
    maxHeight: '68px',
    top: '0px',
    right: '0px !important',
    margin: '0px !important',
    width: 'unset',
    gap: '4px',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    boxShadow: 'none',
    borderRadius: '0px !important',
  },
  itemsGroup: {
    display: 'flex',
    flexDirection: 'row',
  },
});
